import {
  Children,
  isValidElement,
  useMemo,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Modal } from 'antd';

// Shadcn-style Dialog/DialogContent API on top of antd Modal.

interface DialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}

interface DialogContentProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  className?: string;
}

const HEADER_MARK = Symbol.for('aidu.dialog.header');
const FOOTER_MARK = Symbol.for('aidu.dialog.footer');
const CONTENT_MARK = Symbol.for('aidu.dialog.content');
const TITLE_MARK = Symbol.for('aidu.dialog.title');
const DESC_MARK = Symbol.for('aidu.dialog.desc');

type Marked<P> = ((p: P) => ReactNode) & { __mark?: symbol };

function mark<P>(fn: (p: P) => ReactNode, sym: symbol): (p: P) => ReactNode {
  const f = fn as Marked<P>;
  f.__mark = sym;
  return f as never;
}

function flatten(children: ReactNode): ReactElement[] {
  const out: ReactElement[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    out.push(child);
    const c = (child.props as { children?: ReactNode }).children;
    if (c !== undefined) out.push(...flatten(c));
  });
  return out;
}

function find(elements: ReactElement[], sym: symbol): ReactElement | undefined {
  return elements.find((el) => (el.type as Marked<unknown>).__mark === sym);
}

export const DialogTitle = mark(({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={className}>{children}</div>
), TITLE_MARK);

export const DialogDescription = mark(
  ({ children, className }: { children: ReactNode; className?: string }) => (
    <div className={className}>{children}</div>
  ),
  DESC_MARK,
);

export const DialogHeader = mark(({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={className}>{children}</div>
), HEADER_MARK);

export const DialogFooter = mark(({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={className}>{children}</div>
), FOOTER_MARK);

export const DialogPortal = ({ children }: { children: ReactNode }) => <>{children}</>;
export const DialogOverlay = () => null;
export const DialogTrigger = ({ children }: { children: ReactNode }) => <>{children}</>;
export const DialogClose = ({ children }: { children?: ReactNode }) => <>{children}</>;

export const DialogContent = mark(({ children, className }: DialogContentProps) => (
  <div className={className}>{children}</div>
), CONTENT_MARK);

export function Dialog({ open, defaultOpen, onOpenChange, children }: DialogProps) {
  void defaultOpen;
  const elements = useMemo(() => flatten(children), [children]);
  const content = find(elements, CONTENT_MARK);
  if (!content) return null;

  const contentElements = useMemo(
    () => flatten((content.props as DialogContentProps).children),
    [content],
  );
  const header = find(contentElements, HEADER_MARK);
  const title = header ? find(flatten((header.props as { children?: ReactNode }).children), TITLE_MARK) : undefined;
  const desc = header ? find(flatten((header.props as { children?: ReactNode }).children), DESC_MARK) : undefined;
  const footer = find(contentElements, FOOTER_MARK);

  // Body = content children minus header/footer subtrees.
  const headerProp = (header?.props ?? {}) as { children?: ReactNode };
  const footerProp = (footer?.props ?? {}) as { children?: ReactNode };
  const body = Children.toArray((content.props as DialogContentProps).children).filter((node) => {
    if (!isValidElement(node)) return true;
    const type = node.type as Marked<unknown>;
    return type.__mark !== HEADER_MARK && type.__mark !== FOOTER_MARK;
  });

  void headerProp;
  void footerProp;

  return (
    <Modal
      open={open}
      onCancel={() => onOpenChange?.(false)}
      footer={footer ? <div className="flex justify-end gap-2">{(footer.props as { children?: ReactNode }).children}</div> : null}
      title={
        header ? (
          <div>
            {title ? <div className="text-base font-semibold">{(title.props as { children?: ReactNode }).children}</div> : null}
            {desc ? <div className="text-xs opacity-70">{(desc.props as { children?: ReactNode }).children}</div> : null}
          </div>
        ) : null
      }
      destroyOnClose
      centered
    >
      <div className="flex flex-col gap-4">{body}</div>
    </Modal>
  );
}
